// Import analytics utilities at the top
import {
  trackMetaPixelEvent,
  trackMetaInitiateCheckout,
  trackMetaPurchase,
  trackMetaViewContent,
} from './metaPixel'
import { trackTwitterEvent, fireTwitterDepositSuccess } from './twitterPixel'

type DeviceType = 'desktop' | 'mobile'

interface GlobalFields {
  page: string
  session_id: string
  device_type: DeviceType
  country: string
}

type TrackingOverrides = Partial<GlobalFields>

type GtagValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | GtagValue[]
  | { [key: string]: GtagValue }

type GtagEventParams = Record<string, GtagValue>

declare global {
  interface Window {
    gtag?: (command: 'event', eventName: string, params?: GtagEventParams) => void
    $gtag?: {
      event: (eventName: string, params?: GtagEventParams) => void
    }
  }
}

const SESSION_STORAGE_KEY = 'skf_session_id'

let overrides: TrackingOverrides = {}
let inMemorySessionId: string | null = null

const generateSessionId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return `sess_${Math.random().toString(16).slice(2)}_${Date.now()}`
}

const resolveSessionId = (): string => {
  if (overrides.session_id) {
    return overrides.session_id
  }

  if (typeof window === 'undefined') {
    if (!inMemorySessionId) {
      inMemorySessionId = generateSessionId()
    }
    return inMemorySessionId
  }

  try {
    const stored = window.localStorage.getItem(SESSION_STORAGE_KEY)
    if (stored) {
      return stored
    }

    const fresh = generateSessionId()
    window.localStorage.setItem(SESSION_STORAGE_KEY, fresh)
    return fresh
  } catch (error) {
    if (!inMemorySessionId) {
      inMemorySessionId = generateSessionId()
    }
    return inMemorySessionId
  }
}

const resolveDeviceType = (): DeviceType => {
  if (overrides.device_type) {
    return overrides.device_type
  }

  if (typeof navigator === 'undefined') {
    return 'desktop'
  }

  const ua = navigator.userAgent.toLowerCase()
  return /mobile|android|iphone|ipad|ipod/.test(ua) ? 'mobile' : 'desktop'
}

const resolveCountry = (): string => {
  if (overrides.country) {
    return overrides.country
  }

  if (typeof window === 'undefined') {
    return 'unknown'
  }

  try {
    const stored = window.localStorage.getItem('skf_country')
    if (stored) {
      return stored
    }
  } catch (error) {
    // ignore storage errors
  }

  if (typeof navigator !== 'undefined' && navigator.language) {
    const localeParts = navigator.language.split('-')
    if (localeParts.length > 1) {
      return localeParts[1].toUpperCase()
    }
  }

  return 'unknown'
}

const resolvePage = (): string => {
  if (overrides.page) {
    return overrides.page
  }

  if (typeof window === 'undefined') {
    return ''
  }

  const { pathname, search, hash } = window.location
  return `${pathname}${search}${hash}`
}

const getGlobalFields = (): GlobalFields => ({
  page: resolvePage(),
  session_id: resolveSessionId(),
  device_type: resolveDeviceType(),
  country: resolveCountry(),
})

const emitEvent = (eventName: string, params: GtagEventParams): void => {
  const payload = { ...getGlobalFields(), ...params }

  if (typeof window !== 'undefined') {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload)
      } else if (window.$gtag && typeof window.$gtag.event === 'function') {
        window.$gtag.event(eventName, payload)
      } else {
        console.warn('gtag is not initialized', { eventName, payload })
      }
    } catch (error) {
      console.warn('Failed to emit gtag event', { eventName, error })
    }
  }

  trackMetaPixelEvent(eventName, payload)
  trackTwitterEvent(eventName, payload)
}

export const setTrackingOverrides = (context: TrackingOverrides): void => {
  overrides = { ...overrides, ...context }
}

export const getSessionId = (): string => resolveSessionId()

export const trackSteamLoginStart = (): void => {
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem('steam_login_started', '1')
      window.sessionStorage.removeItem('steam_login_success')
    } catch (error) {
      // ignore storage errors
    }
  }
  emitEvent('steam_login_start', { provider: 'steam' })
}

export const trackSteamLoginSuccess = (): void => {
  emitEvent('steam_login_success', { provider: 'steam' })
}

export interface CompleteRegistrationParams extends GtagEventParams {
  provider: string
  steam_id?: string
}

export const trackCompleteRegistration = (params: CompleteRegistrationParams): void => {
  emitEvent('complete_registration', params)
}

export interface SteamLoginFailParams extends GtagEventParams {
  error_code: string
  error_msg: string
}

export const trackSteamLoginFail = (params: SteamLoginFailParams): void => {
  emitEvent('steam_login_fail', params)
}

export interface PaymentMethodSelectParams extends GtagEventParams {
  method: string
}

export const trackPaymentMethodSelect = (params: PaymentMethodSelectParams): void => {
  emitEvent('payment_method_select', params)
}

export interface DepositInitiateParams extends GtagEventParams {
  method: string
  amount: number
  bonus_code_used: boolean | string | null
}

export const trackDepositInitiate = (params: DepositInitiateParams): void => {
  emitEvent('deposit_initiate', params)
}

export interface DepositSuccessParams extends GtagEventParams {
  amount: number
  bonus_percent: number
}

export const trackDepositSuccess = (params: DepositSuccessParams): void => {
  emitEvent('deposit_success', params)
  
  // Track Meta Pixel Purchase event (standard Facebook conversion)
  trackMetaPurchase(params.amount / 100, 'USD', {
    bonus_percent: params.bonus_percent,
  })
  
  // Track Twitter conversion
  fireTwitterDepositSuccess({
    value: params.amount / 100,
    currency: 'USD',
  })
}

export interface DepositFailParams extends GtagEventParams {
  error_code: string
  error_msg?: string
}

export const trackDepositFail = (params: DepositFailParams): void => {
  emitEvent('deposit_fail', params)
}

export interface CaseListViewParams extends GtagEventParams {
  list_id: string
}

export const trackCaseListView = (params: CaseListViewParams): void => {
  emitEvent('case_list_view', params)
}

export interface CaseDetailViewParams extends GtagEventParams {
  case_id: string
  price: number
  is_daily_case: boolean
  is_logged_in: boolean
  is_token_case: boolean
}

export const trackCaseDetailView = (params: CaseDetailViewParams): void => {
  emitEvent('case_detail_view', params)
  trackMetaViewContent(params.case_id, {
    content_type: 'case',
    content_category: params.is_daily_case ? 'daily_case' : 'standard_case',
    value: params.price,
    currency: 'USD',
    is_logged_in: params.is_logged_in,
    is_token_case: params.is_token_case,
  })
}

export interface CaseOpenClickParams extends GtagEventParams {
  case_id: string
  price: number
  balance_before: number
}

export const trackCaseOpenClick = (params: CaseOpenClickParams): void => {
  emitEvent('case_open_click', params)
}

export interface CaseTestSpinParams extends GtagEventParams {
  case_id: string
  amount: number
  is_multi: boolean
}

export const trackCaseTestSpin = (params: CaseTestSpinParams): void => {
  emitEvent('case_test_spin', params)
}

export interface CaseOpenCountClickParams extends GtagEventParams {
  case_open_count: number
}

export const trackCaseOpenCountClick = (params: CaseOpenCountClickParams): void => {
  emitEvent('case_open_count_click', params)
}

export interface CaseOpenResultParams extends GtagEventParams {
  case_id: string
  item_id: string
  item_name: string
  rarity: string
  item_value: number
  payout_ratio: number
  balance_after: number
  status: string
}

export const trackCaseOpenResult = (params: CaseOpenResultParams): void => {
  emitEvent('case_open_result', params)
}

export interface WithdrawSubmitParams extends GtagEventParams {
  method: string
  amount: number
}

export const trackWithdrawSubmit = (params: WithdrawSubmitParams): void => {
  emitEvent('withdraw_submit', params)
}

export interface WithdrawSuccessParams extends GtagEventParams {
  method: string
  amount: number
}

export const trackWithdrawSuccess = (params: WithdrawSuccessParams): void => {
  emitEvent('withdraw_success', params)
}

export interface WithdrawFailParams extends GtagEventParams {
  error_code: string
  error_msg?: string
}

export const trackWithdrawFail = (params: WithdrawFailParams): void => {
  emitEvent('withdraw_fail', params)
}

export const trackSessionStart = (): void => {
  const sessionId = resolveSessionId()
  emitEvent('session_start', { session_id: sessionId })
}

export interface InventoryFilterApplyParams extends GtagEventParams {
  filter_value: string
}

export const trackInventoryFilterApply = (params: InventoryFilterApplyParams): void => {
  emitEvent('inventory_filter_apply', params)
}

export interface PromoCodeApplyParams extends GtagEventParams {
  code: string
  valid: boolean
}

export const trackPromoCodeApply = (params: PromoCodeApplyParams): void => {
  emitEvent('promo_code_apply', params)
}

export interface ItemInventoryViewParams extends GtagEventParams {
  item_count: number
}

export const trackGiveawayPromoClick = (): void => {
  emitEvent('giveaway_promo_clicked', {})
}

export const trackItemInventoryView = (params: ItemInventoryViewParams): void => {
  emitEvent('item_inventory_view', params)
}

export interface ItemActionParams extends GtagEventParams {
  item_id: string
  action_type: 'sell' | 'withdraw'
}

export const trackItemSell = (itemId: string): void => {
  const params: ItemActionParams = { item_id: itemId, action_type: 'sell' }
  emitEvent('item_sell', params)
}

export const trackItemWithdrawClick = (itemId: string): void => {
  const params: ItemActionParams = { item_id: itemId, action_type: 'withdraw' }
  emitEvent('item_withdraw_click', params)
}

export const trackTradeUrlEditClick = (userId: string): void => {
  emitEvent('trade_url_edit_click', { field_type: 'trade_url', user_id: userId })
}

export interface WordleGameLoadParams extends GtagEventParams {
  play_id: string
  guesses_count: number
  attempts_remaining: number
  game_over: boolean
  won: boolean | null
}

export const trackWordleGameLoad = (params: WordleGameLoadParams): void => {
  emitEvent('wordle_game_load', params)
}

export interface WordleGuessSubmitParams extends GtagEventParams {
  play_id: string
  guess_word: string
  guess_number: number
  attempts_remaining: number
  correct_letters: number
  present_letters: number
  game_over: boolean
  won: boolean | null
}

export const trackWordleGuessSubmit = (params: WordleGuessSubmitParams): void => {
  emitEvent('wordle_guess_submit', params)
}

export interface WordleGameResultParams extends GtagEventParams {
  play_id: string
  result: 'win' | 'lose'
  guesses_used: number
  answer_word?: string | null
  attempts_remaining: number
  reward_whale_points: number
  reward_tokens: number
  reward_balance_cents: number
}

export const trackWordleGameResult = (params: WordleGameResultParams): void => {
  emitEvent('wordle_game_result', params)
}

export type { GlobalFields, TrackingOverrides, DeviceType, GtagEventParams, GtagValue }
